/**
 * StudioSwift by WRG - Header Section JavaScript
 * Handles: Mobile menu, dropdowns, search slideout, keyboard navigation
 */

(function () {
  'use strict';

  const Header = {
    selectors: {
      header: '.js-header',
      menuOpen: '.js-menu-open',
      mainNav: '#main-navigation',
      dropdownToggle: '.dropdown-toggle',
      submenuToggle: '.submenu-toggle',
      dropdownHeader: '.dropdown-header',
      subMenu: '.sub-menu',
      searchBtn: '.js-search-btn',
      siteSearch: '.js-site-search',
      searchClose: '.js-search-close',
      searchInput: '#site-search-input',
      searchClear: '#site-search-clear',
      searchResults: '.site-search__results ul'
    },

    classes: {
      menuOpen: 'menu-open',
      searchOpen: 'search-open',
      dropdownOpen: 'is-open',
      bodyNoScroll: 'no-scroll'
    },

    init: function () {
      this.header = document.querySelector(this.selectors.header);
      if (!this.header) return;

      this.bindEvents();
      this.initHeaderHeight();
    },

    initHeaderHeight: function () {
      // Update header height on init
      this.updateHeaderHeight();

      // Update header height on resize (debounced)
      const debouncedUpdate = this.debounce(() => {
        this.updateHeaderHeight();
      }, 100);

      window.addEventListener('resize', debouncedUpdate);
    },

    updateHeaderHeight: function () {
      const header = document.querySelector('.ss.header');
      if (!header) return;

      const height = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    },

    bindEvents: function () {
      // Mobile menu toggle
      const menuToggle = this.header.querySelector(this.selectors.menuOpen);
      if (menuToggle) {
        menuToggle.addEventListener('click', this.toggleMobileMenu.bind(this));
      }

      // Dropdown toggles (desktop navigation dropdowns)
      const dropdownToggles = this.header.querySelectorAll(this.selectors.dropdownToggle);
      dropdownToggles.forEach(toggle => {
        toggle.addEventListener('click', this.toggleDropdown.bind(this));
      });


      // Close desktop dropdowns when hovering items WITHOUT .has-dropdown
      const topLevelItems = this.header.querySelectorAll('.header__nav-main > li');
      topLevelItems.forEach(item => {
        item.addEventListener('mouseenter', () => {
          if (!item.classList.contains('has-dropdown')) {
            this.closeAllDropdowns();
          }
        });
      });



      // Submenu toggles (mobile shop dropdown)
      const submenuToggles = this.header.querySelectorAll(this.selectors.submenuToggle);
      submenuToggles.forEach(toggle => {
        toggle.addEventListener('click', this.toggleSubmenu.bind(this));
      });

      // Search buttons
      const searchBtns = document.querySelectorAll(this.selectors.searchBtn);
      searchBtns.forEach(btn => {
        btn.addEventListener('click', this.openSearch.bind(this));
      });

      // Search close
      const searchClose = document.querySelector(this.selectors.searchClose);
      if (searchClose) {
        searchClose.addEventListener('click', this.closeSearch.bind(this));
      }

      // Search clear
      const searchClear = document.querySelector(this.selectors.searchClear);
      if (searchClear) {
        searchClear.addEventListener('click', this.clearSearch.bind(this));
      }

      // Search input for predictive search
      const searchInput = document.querySelector(this.selectors.searchInput);
      if (searchInput) {
        searchInput.addEventListener('input', this.debounce(this.handleSearchInput.bind(this), 300));
      }

      // Close dropdowns when clicking outside
      document.addEventListener('click', this.handleOutsideClick.bind(this));

      // Keyboard navigation
      document.addEventListener('keydown', this.handleKeydown.bind(this));
    },

    toggleMobileMenu: function (e) {
      const toggle = e.currentTarget;
      const nav = this.header.querySelector(this.selectors.mainNav);
      const mobileMenu = this.header.querySelector('.header__nav--primary');
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';

      // Close mobile shop dropdown when opening hamburger menu
      if (!isOpen) {
        this.closeAllSubmenus();
      }

      toggle.setAttribute('aria-expanded', !isOpen);
      this.header.classList.toggle(this.classes.menuOpen, !isOpen);

      // Toggle is-open class on mobile menu
      if (mobileMenu) {
        mobileMenu.classList.toggle('is-open', !isOpen);
      }

      // Update header height after menu toggle (header height might change)
      setTimeout(() => this.updateHeaderHeight(), 0);

      if (!isOpen && nav) {
        nav.querySelector('a, button')?.focus();
      }
    },

    toggleDropdown: function (e) {
      const toggle = e.currentTarget;
      const dropdownHeader = toggle.closest(this.selectors.dropdownHeader);
      const parentLi = toggle.closest('li');
      const subMenu = parentLi.querySelector(this.selectors.subMenu);
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';

      // Close other dropdowns
      this.closeAllDropdowns(parentLi);

      // Toggle current dropdown
      toggle.setAttribute('aria-expanded', !isOpen);
      parentLi.classList.toggle(this.classes.dropdownOpen, !isOpen);

      if (subMenu) {
        subMenu.setAttribute('aria-hidden', isOpen);
      }
    },


    toggleSubmenu: function (e) {
      const toggle = e.currentTarget;
      const parentLi = toggle.closest('li');
      const subMenu = parentLi.querySelector(this.selectors.subMenu);
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';

      // Close hamburger menu when opening shop dropdown
      if (!isOpen) {
        this.closeMobileMenu();
      }

      // Close other submenus
      this.closeAllSubmenus(parentLi);

      // Toggle current submenu
      toggle.setAttribute('aria-expanded', !isOpen);
      parentLi.classList.toggle(this.classes.dropdownOpen, !isOpen);

      if (subMenu) {
        subMenu.setAttribute('aria-hidden', isOpen);
      }
    },

    closeMobileMenu: function () {
      const menuToggle = this.header.querySelector(this.selectors.menuOpen);
      const mobileMenu = this.header.querySelector('.header__nav--primary');

      if (menuToggle && menuToggle.getAttribute('aria-expanded') === 'true') {
        menuToggle.setAttribute('aria-expanded', 'false');
        this.header.classList.remove(this.classes.menuOpen);

        if (mobileMenu) {
          mobileMenu.classList.remove('is-open');
        }
      }
    },

    closeAllSubmenus: function (except = null) {
      const allSubmenus = this.header.querySelectorAll('.has-dropdown.is-mobile');
      allSubmenus.forEach(submenu => {
        if (submenu !== except) {
          const toggle = submenu.querySelector(this.selectors.submenuToggle);
          const subMenu = submenu.querySelector(this.selectors.subMenu);

          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          if (subMenu) subMenu.setAttribute('aria-hidden', 'true');
          submenu.classList.remove(this.classes.dropdownOpen);
        }
      });
    },

    closeAllDropdowns: function (except = null) {
      const allDropdowns = this.header.querySelectorAll('.has-dropdown.is-desktop');
      allDropdowns.forEach(dropdown => {
        if (dropdown !== except) {
          const toggle = dropdown.querySelector(this.selectors.dropdownToggle);
          const subMenu = dropdown.querySelector(this.selectors.subMenu);

          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          if (subMenu) subMenu.setAttribute('aria-hidden', 'true');
          dropdown.classList.remove(this.classes.dropdownOpen);
        }
      });
    },

    openSearch: function (e) {
      e.preventDefault();
      const siteSearch = document.querySelector(this.selectors.siteSearch);
      const searchInput = document.querySelector(this.selectors.searchInput);

      if (siteSearch) {
        siteSearch.setAttribute('aria-hidden', 'false');
        siteSearch.classList.add(this.classes.searchOpen);
        document.body.classList.add(this.classes.bodyNoScroll);

        if (searchInput) {
          setTimeout(() => searchInput.focus(), 100);
        }
      }
    },

    closeSearch: function () {
      const siteSearch = document.querySelector(this.selectors.siteSearch);

      if (siteSearch) {
        siteSearch.setAttribute('aria-hidden', 'true');
        siteSearch.classList.remove(this.classes.searchOpen);
        document.body.classList.remove(this.classes.bodyNoScroll);
      }
    },

    clearSearch: function () {
      const searchInput = document.querySelector(this.selectors.searchInput);
      const searchResults = document.querySelector(this.selectors.searchResults);

      if (searchInput) {
        searchInput.value = '';
        searchInput.focus();
      }

      if (searchResults) {
        searchResults.innerHTML = '';
      }
    },

    handleSearchInput: function (e) {
      const query = e.target.value.trim();

      if (query.length < 2) {
        this.clearSearchResults();
        return;
      }

      this.fetchPredictiveSearch(query);
    },

    fetchPredictiveSearch: function (query) {
      const searchResults = document.querySelector(this.selectors.searchResults);
      if (!searchResults) return;

      // Shopify Predictive Search API
      fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4`)
        .then(response => response.json())
        .then(data => {
          this.renderSearchResults(data.resources.results.products || []);
        })
        .catch(error => {
          console.error('Search error:', error);
        });
    },

    renderSearchResults: function (products) {
      const searchResults = document.querySelector(this.selectors.searchResults);
      if (!searchResults) return;

      if (products.length === 0) {
        searchResults.innerHTML = '<li class="no-results">No results found</li>';
        return;
      }

      // const html = products.map(product => `
      //   <li>
      //     <div class="ss search-result">
      //       <figure>
      //         <img 
      //           src="${product.image || ''}" 
      //           width="135" 
      //           height="135" 
      //           alt="${product.title}"
      //           loading="lazy"
      //         >
      //       </figure>
      //       <article>
      //         <h3>${product.title}</h3>
      //         <a class="l-link l-link--md" href="${product.url}" role="menuitem">View Product</a>
      //       </article>
      //     </div>
      //   </li>
      // `).join('');

      const placeholderSVG = document.querySelector('.product-card__image .placeholder-svg')?.outerHTML || '';

      const html = products.map(product => `
        <li>
          <div class="ss search-result">
            <figure>
              ${product.image
          ? `<img 
                    src="${product.image}" 
                    width="135" 
                    height="135" 
                    alt="${product.title}"
                    loading="lazy"
                  >`
          : placeholderSVG
        }
            </figure>
            <article>
              <h3>${product.title}</h3>
              <a class="l-link l-link--md" href="${product.url}" role="menuitem">View Product</a>
            </article>
          </div>
        </li>
      `).join('');

      searchResults.innerHTML = html;
    },

    clearSearchResults: function () {
      const searchResults = document.querySelector(this.selectors.searchResults);
      if (searchResults) {
        searchResults.innerHTML = '';
      }
    },

    handleOutsideClick: function (e) {
      // Close desktop dropdowns when clicking outside
      if (!e.target.closest('.has-dropdown.is-desktop')) {
        this.closeAllDropdowns();
      }

      // Close mobile submenus when clicking outside
      if (!e.target.closest('.has-dropdown.is-mobile')) {
        this.closeAllSubmenus();
      }

      // Close search when clicking outside (but not on search button)
      const siteSearch = document.querySelector(this.selectors.siteSearch);
      if (siteSearch &&
        siteSearch.classList.contains(this.classes.searchOpen) &&
        !e.target.closest(this.selectors.siteSearch) &&
        !e.target.closest(this.selectors.searchBtn)) {
        this.closeSearch();
      }
    },

    handleKeydown: function (e) {
      // Close on Escape
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
        this.closeAllSubmenus();
        this.closeSearch();

        // Close mobile menu
        this.closeMobileMenu();
      }
    },

    debounce: function (fn, wait) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }


  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Header.init());
  } else {
    Header.init();
  }

  // ✅ Set height as early as possible to prevent flicker
  document.addEventListener('DOMContentLoaded', function () {
    const header = document.querySelector('.ss.header');
    if (header) {
      document.documentElement.style.setProperty(
        '--header-height',
        header.offsetHeight + 'px'
      );
    }
  });

  // Re-initialize on Shopify section reload (Theme Editor)
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('.js-header')) {
      Header.init();
    }
  });

})();
